import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useMemo, useRef, useState } from "react"
import {AlertCircle, CreditCard, DollarSign, Plus, Search, Trash2, TrendingUp} from "lucide-react"

import Pagination from "@/components/pagination.tsx"

import { DeviceFiltersType, DeviceType, PaginationType } from "./types.ts"

interface IndexProps {
  devices: DeviceType[]
  filters: DeviceFiltersType
  pagination: PaginationType
  can_destroy?: boolean
}

export default function Index({ devices, filters, pagination, can_destroy = false }: IndexProps) {
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/admin/devices", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const handleDestroy = (device: DeviceType) => {
    if (!window.confirm(`Supprimer le terminal "${device.name}" ?`)) return

    router.delete(`/admin/devices/${device.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Devices" />
      <div className="space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Les terminaux</h2>
            <p className="text-muted-foreground">Gestion des terminaux de collecte</p>
          </div>
          <Link href="/admin/devices/new">
            <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4" />
              Ajouter un terminal
            </button>
          </Link>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Terminaux POS actifs</p>
                <p className="text-2xl font-bold text-foreground">0</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collectes POS du jour</p>
                <p className="text-2xl font-bold text-foreground">0 FCFA</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent/10 p-2">
                <TrendingUp className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total des transactions</p>
                <p className="text-2xl font-bold text-foreground">0</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-warning/10 p-2">
                <AlertCircle className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Terminaux batterie faible</p>
                <p className="text-2xl font-bold text-foreground">1</p>
              </div>
            </div>
          </div>
        </div>


        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                  type="text"
                  placeholder="Rechercher par ID terminal, nom d'agent ou numéro de série..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background py-2 pl-10 pr-4 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="space-y-3">
            {devices.map((device) => (
                <div
                  key={device.id}
                  className="cursor-pointer rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50"
                  role="button"
                  tabIndex={0}
                  onClick={() => router.visit(`/admin/devices/${device.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault()
                      router.visit(`/admin/devices/${device.id}`)
                    }
                  }}
                >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-semibold text-foreground">{device.name}</p>
                          <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                  device.status === "Active" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
                              }`}
                          >
                        {device.status === "Active"
                          ? "Actif"
                          : device.status === "Assigned"
                            ? "Assigné"
                            : device.status === "blocked"
                              ? "Bloqué"
                              : device.status}
                      </span>
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
                        {device.serial_number}
                      </span>
                        </div>

                        <p className="mt-1 text-sm text-muted-foreground">
                          Agent : {device.assigned_agent_name || "Non assigné"}
                        </p>

                        <div className="mt-3 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                          <div>
                            <p className="text-xs text-muted-foreground">Total Paiements</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">{device.total_payments_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Montant du jour</p>
                            <p className="mt-1 text-sm font-semibold text-success">
                              {Number(device.today_collected_amount || 0).toFixed(2)} FCFA
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Niveau de batterie</p>
                            <p
                                className={`mt-1 text-sm font-semibold ${56 < 50 ? "text-warning" : "text-foreground"}`}
                            >
                              56%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Dernière collecte</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">
                              {device.last_collection_at ? new Date(device.last_collection_at).toLocaleString() : "-"}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Statut de collecte</p>
                            <span
                              className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                device.last_collection_status === "open"
                                  ? "bg-success/10 text-success"
                                  : device.last_collection_status === "reconciled"
                                    ? "bg-accent/10 text-accent"
                                    : device.last_collection_status === "closed"
                                      ? "bg-muted"
                                      : "bg-muted"
                              }`}
                            >
                              {device.last_collection_status === "open"
                                ? "Actif"
                                : device.last_collection_status === "closed"
                                  ? "Cloturé"
                                  : device.last_collection_status === "reconciled"
                                    ? "Rapproché"
                                    : "-"}
                            </span>
                          </div>
                        </div>
                      </div>
                      {can_destroy && (
                        <button
                          type="button"
                          className="ml-4 rounded-md p-2 text-destructive hover:bg-destructive/10"
                          aria-label={`Supprimer ${device.name}`}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleDestroy(device)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
            ))}
          </div>
        </div>


        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          basePath="/admin/devices"
          query={query}
        />
      </div>
    </>
  )
}
